<!-- PrimeNG Confirmation Dialog -->
<p-confirm-dialog></p-confirm-dialog>

@if (license(); as lic) {
<div class="license-view-container p-4" style="position: relative;">
    <!-- Loading Overlay for Certificate Regeneration -->
    @if (regeneratingCertificate()) {
        <div class="certificate-loading-overlay">
            <div class="flex flex-column align-items-center">
                <p-progressspinner></p-progressspinner>
                <p class="mt-3 text-600">Regenerating certificate...</p>
            </div>
        </div>
    }
    <!-- Actions Bar -->
    <div class="flex justify-content-between align-items-center pb-1 mb-3 mt-1 border-bottom-1 border-300">
        <h2 class="m-0 text-lg font-semibold">License Information</h2>
        <div class="flex gap-2">
            @if (canActivate()) {
                <button
                    pButton
                    type="button"
                    class="p-button-text p-button-sm"
                    (click)="onActivate()"
                    pTooltip="Grant license - assign number and expiry date"
                    tooltipPosition="top"><i class="pi pi-check-circle" pButtonIcon></i><span pButtonLabel>Grant</span>
                </button>
            }
            @if (canExtend()) {
                <button
                    pButton
                    type="button"
                    class="p-button-text p-button-sm"
                    (click)="onExtend()"
                    pTooltip="Renew license expiry date"
                    tooltipPosition="top"><i class="pi pi-calendar-plus" pButtonIcon></i><span pButtonLabel>Renew</span>
                </button>
            }
            @if (hasVisibleActions()) {
                <app-actions-menu
                    [items]="actionMenuItems()"
                    tooltip="More actions"
                    testId="license-actions-menu">
                </app-actions-menu>
            }
        </div>
    </div>

    <!-- Basic Information Section -->
    <div class="mb-4">
        <div class="grid">
            <div class="col-12 md:col-6">
                <div class="field mb-3">
                    <label class="block text-sm font-medium text-600 mb-1">License Number</label>
                    <div class="text-900 font-medium">{{ lic.completeLicenseNumber }}</div>
                </div>
                <div class="field mb-3">
                    <label class="block text-sm font-medium text-600 mb-1">License Type</label>
                    <div class="text-900">{{ lic.licenseType || '-' }}</div>
                </div>
                <div class="field mb-3">
                    <label class="block text-sm font-medium text-600 mb-1">Date of Granting</label>
                    <div class="text-900">{{ formatDate(lic.issueDate) }}</div>
                </div>
                <div class="field mb-3">
                    <label class="block text-sm font-medium text-600 mb-1">Renewal Count</label>
                    <div class="text-900">{{ lic.extensionCount || 0 }}</div>
                </div>
            </div>
            <div class="col-12 md:col-6">
                <div class="field mb-3">
                    <label class="block text-sm font-medium text-600 mb-1">Status</label>
                    <p-tag
                        [value]="getLicenseStatus().label"
                        [severity]="getLicenseStatus().severity"
                        [rounded]="true">
                    </p-tag>
                </div>
                <div class="field mb-3">
                    <label class="block text-sm font-medium text-600 mb-1">Expiry Date</label>
                    <div class="text-900">{{ formatDate(lic.expiryDate) }}</div>
                </div>
                <div class="field mb-3">
                    <label class="block text-sm font-medium text-600 mb-1">Last Renewal Date</label>
                    <div class="text-900">{{ formatDate(lic.lastExtensionDate) }}</div>
                </div>
                @if (lic.withdrawalDate) {
                    <div class="field mb-3">
                        <label class="block text-sm font-medium text-600 mb-1">Withdrawal Date</label>
                        <div class="text-900">{{ formatDate(lic.withdrawalDate) }}</div>
                    </div>
                }
                @if (lic.withdrawalReason) {
                    <div class="field mb-3">
                        <label class="block text-sm font-medium text-600 mb-1">Withdrawal Reason</label>
                        <div class="text-900">{{ lic.withdrawalReason }}</div>
                    </div>
                }
                @if (lic.suspensionDate) {
                    <div class="field mb-3">
                        <label class="block text-sm font-medium text-600 mb-1">Suspension Date</label>
                        <div class="text-900">{{ formatDate(lic.suspensionDate) }}</div>
                    </div>
                }
                @if (lic.suspensionReason) {
                    <div class="field mb-3">
                        <label class="block text-sm font-medium text-600 mb-1">Suspension Reason</label>
                        <div class="text-900">{{ lic.suspensionReason }}</div>
                    </div>
                }
            </div>
        </div>
    </div>

    <!-- Production Site Information Section -->
    @if (lic.productionSite) {
        <div class="mb-4">
            <div class="flex justify-content-between align-items-center mb-3">
                <h3 class="text-lg font-semibold m-0">
                    <fa-icon icon="industry" class="mr-2"></fa-icon>
                    Production Site
                </h3>
                @if (canEditLineEndorsements()) {
                    <button
                        pButton
                        type="button"
                        class="p-button-text p-button-sm"
                        (click)="onEditProductionSite()"><i class="pi pi-pencil" pButtonIcon></i><span pButtonLabel>Edit Production Site</span>
                    </button>
                }
            </div>
            <p-divider></p-divider>
            <div class="grid">
                <div class="col-12 md:col-6">
                    <div class="field mb-3">
                        <label class="block text-sm font-medium text-600 mb-1">Site Name</label>
                        <div class="text-900">{{ lic.productionSite.name || '-' }}</div>
                    </div>
                    <div class="field mb-3">
                        <label class="block text-sm font-medium text-600 mb-1">Main Site</label>
                        <div class="text-900">
                            <p-tag
                                [value]="lic.productionSite.isMainProductionSite ? 'Yes' : 'No'"
                                [severity]="lic.productionSite.isMainProductionSite ? 'success' : 'secondary'"
                                [rounded]="true">
                            </p-tag>
                        </div>
                    </div>
                </div>
                <div class="col-12 md:col-6">
                    @if (lic.productionSite.customer) {
                        <div class="field mb-3">
                            <label class="block text-sm font-medium text-600 mb-1">Customer</label>
                            <div class="text-900">{{ lic.productionSite.customer.name || '-' }}</div>
                        </div>
                    }
                    <div class="field mb-3">
                        <label class="block text-sm font-medium text-600 mb-1">Production Site Address</label>
                        <div class="surface-card p-3 border-round shadow-2" style="width: calc(100% - 0.75rem); min-width: 250px;">
                            <div class="flex justify-content-between align-items-start">
                                <div class="flex-grow-1">
                                    @if (lic.productionSite.addressLine1) {
                                        <div class="text-900">{{ lic.productionSite.addressLine1 }}</div>
                                    }
                                    @if (lic.productionSite.addressLine2) {
                                        <div class="text-900">{{ lic.productionSite.addressLine2 }}</div>
                                    }
                                    @if (lic.productionSite.addressLine3) {
                                        <div class="text-900">{{ lic.productionSite.addressLine3 }}</div>
                                    }
                                    @if (lic.productionSite.postalCode || lic.productionSite.city) {
                                        <div class="text-900 mt-2">
                                            {{ lic.productionSite.postalCode }} {{ lic.productionSite.city }}
                                            @if (lic.productionSite.state) {
                                                <span>, {{ lic.productionSite.state }}</span>
                                            }
                                        </div>
                                    }
                                    @if (lic.productionSite.countryName) {
                                        <div class="text-900">{{ lic.productionSite.countryName }}</div>
                                    }
                                </div>
                                <button
                                    pButton
                                    type="button"
                                    class="p-button-text p-button-sm p-button-rounded ml-2"
                                    style="width: 1.75rem; height: 1.75rem; min-width: 1.75rem; margin-top: -5px;"
                                    pTooltip="Show on map"
                                    tooltipPosition="top"
                                    (click)="openMap()"><i class="pi pi-map-marker" pButtonIcon></i>
                                </button>
                            </div>
                        </div>
                    </div>
                    @if (lic.productionSite.contact) {
                        <div class="field mb-3">
                            <label class="block text-sm font-medium text-600 mb-1">Contact Person</label>
                            <div class="surface-card p-3 border-round shadow-2" style="width: calc(100% - 0.75rem); min-width: 250px;">
                                <div class="flex justify-content-between align-items-start">
                                    <div class="flex-grow-1">
                                        @if (lic.productionSite.contact.salutation || lic.productionSite.contact.firstName || lic.productionSite.contact.lastName) {
                                            <div class="text-900 font-semibold mb-2">
                                                {{ lic.productionSite.contact.salutation }} {{ lic.productionSite.contact.firstName }} {{ lic.productionSite.contact.lastName }}
                                            </div>
                                        }
                                        @if (lic.productionSite.contact.title) {
                                            <div class="text-700 text-sm mb-1">
                                                {{ lic.productionSite.contact.title }}
                                            </div>
                                        }
                                        @if (lic.productionSite.contact.email) {
                                            <div class="text-900 text-sm">
                                                <i class="pi pi-envelope mr-2"></i>{{ lic.productionSite.contact.email }}
                                            </div>
                                        }
                                        @if (lic.productionSite.contact.phone) {
                                            <div class="text-900 text-sm">
                                                <i class="pi pi-phone mr-2"></i>{{ lic.productionSite.contact.phone }}
                                            </div>
                                        }
                                        @if (lic.productionSite.contact.mobile) {
                                            <div class="text-900 text-sm">
                                                <i class="pi pi-mobile mr-2"></i>{{ lic.productionSite.contact.mobile }}
                                            </div>
                                        }
                                    </div>
                                    @if (lic.productionSite.contact.email) {
                                        <button
                                            pButton
                                            type="button"
                                            class="p-button-text p-button-sm p-button-rounded ml-2"
                                            style="width: 1.75rem; height: 1.75rem; min-width: 1.75rem; margin-top: -5px;"
                                            pTooltip="Copy email"
                                            tooltipPosition="top"
                                            (click)="sendEmail(lic.productionSite.contact.email)"><i class="pi pi-envelope" pButtonIcon></i>
                                        </button>
                                    }
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>
    }

    <!-- Lines Endorsements Section -->
    @if (lic.licenseType === 'Coater License') {
        <div class="mb-4">
            <div class="flex justify-content-between align-items-center mb-3">
                <h3 class="text-lg font-semibold m-0">
                    <fa-icon icon="sitemap" class="mr-2"></fa-icon>
                    Lines Endorsements {{ lineExtensions().length > 0 ? '(' + lineExtensions().length + ')' : '' }}
                </h3>
                @if (canEditLineEndorsements()) {
                    <button
                        pButton
                        type="button"
                        class="p-button-text p-button-sm"
                        (click)="onEditLineEndorsements()"
                        pTooltip="Edit line endorsements"
                        tooltipPosition="top"><i class="pi pi-pencil" pButtonIcon></i><span pButtonLabel>Edit Lines</span>
                    </button>
                }
            </div>
            <p-divider></p-divider>

            <!-- Mismatch Warning -->
            @if (linesMismatch()) {
                <div class="surface-ground border-round border-1 border-yellow-500 p-3 mb-3">
                    <div class="flex align-items-start">
                        <i class="pi pi-exclamation-triangle text-yellow-700 text-2xl mr-3"></i>
                        <div class="flex-1">
                            <div class="font-semibold text-900 mb-2">Line Endorsement Mismatch Detected</div>
                            @if (linesMismatch()!.missingEndorsements.length > 0) {
                                <div class="mb-2">
                                    <div class="text-sm text-700 font-medium mb-1">Production site lines not endorsed on this license:</div>
                                    <ul class="text-sm text-700 pl-4 mb-0">
                                        @for (line of linesMismatch()!.missingEndorsements; track line.id) {
                                            <li>{{ line.designation }}</li>
                                        }
                                    </ul>
                                </div>
                            }
                            @if (linesMismatch()!.orphanedEndorsements.length > 0) {
                                <div class="mb-2">
                                    <div class="text-sm text-700 font-medium mb-1">Endorsed lines no longer exist on production site:</div>
                                    <ul class="text-sm text-700 pl-4 mb-0">
                                        @for (endorsement of linesMismatch()!.orphanedEndorsements; track endorsement.lineId) {
                                            <li>{{ endorsement.designation }}</li>
                                        }
                                    </ul>
                                </div>
                            }
                            @if (linesMismatch()!.outdatedEndorsements.length > 0) {
                                <div class="mb-2">
                                    <div class="text-sm text-700 font-medium mb-1">Line configuration changed on production site:</div>
                                    <ul class="text-sm text-700 pl-4 mb-0">
                                        @for (endorsement of linesMismatch()!.outdatedEndorsements; track endorsement.lineId) {
                                            <li>{{ endorsement.designation }}</li>
                                        }
                                    </ul>
                                </div>
                            }
                            <div class="text-sm text-700 mt-2">
                                Use the <b>Edit Lines</b> button to synchronize endorsements with the production site.
                            </div>
                        </div>
                    </div>
                </div>
            }

            <!-- Long Line Names Warning -->
            @if (longLineNames().length > 0) {
                <div class="surface-ground border-round border-1 border-yellow-500 p-3 mb-3">
                    <div class="flex align-items-start">
                        <i class="pi pi-exclamation-triangle text-yellow-700 text-2xl mr-3"></i>
                        <div class="flex-1">
                            <div class="font-semibold text-900 mb-2">Line Name Length Warning</div>
                            <div class="mb-2">
                                <div class="text-sm text-700 mb-1">The following lines have designations longer than {{ maxLineNameLength() }} characters and will be truncated on the certificate:</div>
                                <ul class="text-sm text-700 pl-4 mb-0">
                                    @for (line of longLineNames(); track line.lineId) {
                                        <li>{{ line.designation }} ({{ line.length }} characters)</li>
                                    }
                                </ul>
                                <div class="text-sm text-700 mt-2">To edit these line names, please update the production site coating lines configuration<br>
                                    (accessible using the <b>Edit Production Site</b> button above).</div>
                            </div>
                        </div>
                    </div>
                </div>
            }

            @if (lineExtensions().length > 0) {
                <div>
                    <p-table
                        [value]="lineExtensions()"
                        [scrollable]="true"
                        scrollHeight="200px"
                        styleClass="p-datatable-sm">
                        <ng-template #header>
                            <tr>
                                <th style="width: 50%">Designation</th>
                                <th style="width: 25%">Pretreatment</th>
                                <th style="width: 25%">Extensions</th>
                            </tr>
                        </ng-template>
                        <ng-template #body let-line>
                            <tr>
                                <td>{{ line.designation || '-' }}</td>
                                <td>
                                    <p-tag
                                        [value]="getPretreatmentType(line)"
                                        severity="success"
                                        [rounded]="true">
                                    </p-tag>
                                </td>
                                <td>
                                    @if (line.seaside) {
                                        <fa-icon
                                            [icon]="faWave"
                                            pTooltip="Seaside Extension"
                                            tooltipPosition="top"
                                            class="text-xl mr-2 cursor-help">
                                        </fa-icon>
                                    }
                                    @if (line.anodicPretreatment) {
                                        <img
                                            src="assets/icons/preox_icon.svg"
                                            alt="Anodic extension"
                                            pTooltip="Pre-Ox Extension"
                                            tooltipPosition="top"
                                            style="width: 1.5em; height: 1.3em; margin-right: 0.5rem;"
                                            class="cursor-help">
                                    }
                                    @if (line.postforming) {
                                        <fa-icon
                                            [icon]="faSandwich"
                                            pTooltip="Postforming Extension"
                                            tooltipPosition="top"
                                            class="text-xl cursor-help">
                                        </fa-icon>
                                    }
                                    @if (!line.seaside && !line.anodicPretreatment && !line.postforming) {
                                        <span class="text-500">-</span>
                                    }
                                </td>
                            </tr>
                        </ng-template>
                        <ng-template #emptymessage>
                            <tr>
                                <td colspan="3" class="text-center">No line extensions found</td>
                            </tr>
                        </ng-template>
                    </p-table>
                </div>
            } @else {
                <div class="text-center text-500 p-4">
                    <i class="pi pi-inbox text-4xl mb-3 block"></i>
                    <div class="mb-2">No line endorsements configured</div>
                    @if (canEditLineEndorsements()) {
                        <div class="text-sm">
                            Click "Edit Lines" to add coating lines to this license
                        </div>
                    }
                </div>
            }
        </div>
    }

    <!-- Certificates Section -->
    <div class="mb-4">
        <h3 class="text-lg font-semibold mb-3">
            <fa-icon icon="certificate" class="mr-2"></fa-icon>
            Certificates
        </h3>
        <p-divider></p-divider>

        <div class="certificates-list">
            @if (latestCertificate(); as certificate) {
                <div class="surface-100 border-round overflow-hidden mb-2">
                    <div class="p-3 flex align-items-center gap-3">
                        <div class="flex align-items-center justify-content-center border-round bg-blue-100"
                             style="width: 3rem; height: 3rem;">
                            <i class="pi pi-file-pdf text-xl text-blue-600"></i>
                        </div>
                        <div class="flex-1">
                            <div class="font-medium mb-1">
                                {{ certificate.fileName }}
                            </div>
                            <div class="text-400 text-sm">
                                <span class="mr-3">{{ certificate.documentType }}</span>
                                <span class="mr-3">{{ formatFileSize(certificate.fileSize) }}</span>
                                <span class="mr-3">{{ formatDate(certificate.createdAt) }}</span>
                                <span>{{ getCertificationYear(certificate) }}</span>
                            </div>
                        </div>
                        <div class="flex gap-2">
                            @if (certificate.objectStoreRef) {
                                <p-button
                                    icon="pi pi-share-alt"
                                    severity="secondary"
                                    [text]="true"
                                    [rounded]="true"
                                    pTooltip="Share certificate"
                                    tooltipPosition="top"
                                    (onClick)="toggleCertificateTokens(certificate.id)">
                                </p-button>
                                <p-button
                                    icon="pi pi-eye"
                                    severity="secondary"
                                    [text]="true"
                                    [rounded]="true"
                                    pTooltip="View certificate"
                                    tooltipPosition="top"
                                    (onClick)="viewCertificate(certificate)">
                                </p-button>
                                <p-button
                                    icon="pi pi-download"
                                    severity="secondary"
                                    [text]="true"
                                    [rounded]="true"
                                    pTooltip="Download certificate"
                                    tooltipPosition="top"
                                    (onClick)="downloadCertificate(certificate)">
                                </p-button>
                                <app-language-dropdown
                                    (languageSelected)="downloadCertificateInLanguage($event)">
                                </app-language-dropdown>
                            }
                        </div>
                    </div>
                    @if (expandedCertificateTokens() === certificate.id) {
                        <div class="p-3 surface-50 border-top-1 surface-border">
                            <app-certificate-tokens
                                [licenseId]="lic.id"
                                [license]="lic"
                                [certificateDocument]="certificate"
                                (tokensUpdated)="onCertificateTokensUpdated()" />
                        </div>
                    }
                </div>

                @if (olderCertificates().length > 0) {
                    <p-accordion>
                        <p-accordion-panel value="older">
                            <p-accordion-header>
                                Previous versions ({{ olderCertificates().length }})
                            </p-accordion-header>
                            <p-accordion-content>
                                @for (olderCert of olderCertificates(); track olderCert.id) {
                                    <div class="surface-100 border-round overflow-hidden mb-2">
                                        <div class="p-3 flex align-items-center gap-3">
                                            <div class="flex align-items-center justify-content-center border-round bg-blue-100"
                                                 style="width: 3rem; height: 3rem;">
                                                <i class="pi pi-file-pdf text-xl text-blue-600"></i>
                                            </div>
                                            <div class="flex-1">
                                                <div class="font-medium mb-1">
                                                    {{ olderCert.fileName }}
                                                </div>
                                                <div class="text-400 text-sm">
                                                    <span class="mr-3">{{ olderCert.documentType }}</span>
                                                    <span class="mr-3">{{ formatFileSize(olderCert.fileSize) }}</span>
                                                    <span class="mr-3">{{ formatDate(olderCert.createdAt) }}</span>
                                                    <span>{{ getCertificationYear(olderCert) }}</span>
                                                </div>
                                            </div>
                                            <div class="flex gap-2">
                                                @if (olderCert.objectStoreRef) {
                                                    <p-button
                                                        icon="pi pi-share-alt"
                                                        severity="secondary"
                                                        [text]="true"
                                                        [rounded]="true"
                                                        pTooltip="Share certificate"
                                                        tooltipPosition="top"
                                                        (onClick)="toggleCertificateTokens(olderCert.id)">
                                                    </p-button>
                                                    <p-button
                                                        icon="pi pi-eye"
                                                        severity="secondary"
                                                        [text]="true"
                                                        [rounded]="true"
                                                        pTooltip="View certificate"
                                                        tooltipPosition="top"
                                                        (onClick)="viewCertificate(olderCert)">
                                                    </p-button>
                                                    <p-button
                                                        icon="pi pi-download"
                                                        severity="secondary"
                                                        [text]="true"
                                                        [rounded]="true"
                                                        pTooltip="Download certificate"
                                                        tooltipPosition="top"
                                                        (onClick)="downloadCertificate(olderCert)">
                                                    </p-button>
                                                    <app-language-dropdown
                                                        (languageSelected)="downloadCertificateInLanguage($event)">
                                                    </app-language-dropdown>
                                                }
                                            </div>
                                        </div>
                                        @if (expandedCertificateTokens() === olderCert.id) {
                                            <div class="p-3 surface-50 border-top-1 surface-border">
                                                <app-certificate-tokens
                                                    [licenseId]="lic.id"
                                                    [license]="lic"
                                                    [certificateDocument]="olderCert"
                                                    (tokensUpdated)="onCertificateTokensUpdated()" />
                                            </div>
                                        }
                                    </div>
                                }
                            </p-accordion-content>
                        </p-accordion-panel>
                    </p-accordion>
                }
            } @else {
                <div class="text-center text-500 p-4">
                    <i class="pi pi-inbox text-4xl mb-3"></i>
                    <div>No certificates available</div>
                </div>
            }
        </div>
    </div>

    <!-- Bills Section -->
    <div class="mb-4">
        <app-bills-table [licenseId]="lic.id"></app-bills-table>
    </div>

    <!-- Archive Information -->
    @if (lic.isArchived) {
        <div class="mb-4">
            <h3 class="text-lg font-semibold mb-3">
                <fa-icon icon="inbox" class="mr-2"></fa-icon>
                Archive Information
            </h3>
            <p-divider></p-divider>
            <div class="grid">
                <div class="col-12 md:col-6">
                    <div class="field mb-3">
                        <label class="block text-sm font-medium text-600 mb-1">Archived Date</label>
                        <div class="text-900">{{ formatDate(lic.archivedAt) }}</div>
                    </div>
                </div>
                <div class="col-12 md:col-6">
                    <div class="field mb-3">
                        <label class="block text-sm font-medium text-600 mb-1">Archived By</label>
                        <div class="text-900">{{ lic.archivedBy || '-' }}</div>
                    </div>
                </div>
            </div>
        </div>
    }
</div>
}
